import React, {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import { resetAiUsage } from "./aiAccessApi";

const counter = (value) => (
  Number.isSafeInteger(value) && value >= 0 ? value : 0
);

const resetKey = () => (
  globalThis.crypto?.randomUUID?.()
    || `ai-reset-${Date.now()}-${Math.floor(Math.random() * 1e6)}`
);

const normalizedError = (error) => ({
  status: Number(error?.status) || 0,
  code: typeof error?.code === "string" ? error.code : "request_failed",
  retryable: error?.retryable === true,
  message: typeof error?.message === "string"
    ? error.message
    : "Não foi possível repor a cota de IA.",
});

const AiQuotaReset = ({ companyId, work, usage, onReset }) => {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [intent, setIntent] = useState(null);
  const scopeCompanyId = companyId ?? work.companyId ?? "";
  const resetScope = `${scopeCompanyId}:${work.workId}:${usage?.periodKey ?? ""}`;
  const resetScopeRef = useRef(resetScope);
  const generationRef = useRef(0);
  const mountedRef = useRef(false);
  const consumed = counter(usage?.consumed);
  const reserved = counter(usage?.reserved);
  const lifetimeConsumed = Number.isSafeInteger(usage?.lifetimeConsumed) && usage.lifetimeConsumed >= 0
    ? usage.lifetimeConsumed
    : consumed;
  const manualResetCount = counter(usage?.manualResetCount);
  const canReset = work.enabled
    && usage?.unlimited !== true
    && consumed > 0
    && typeof usage?.periodKey === "string"
    && usage.periodKey.length > 0;

  useLayoutEffect(() => {
    if (resetScopeRef.current === resetScope) return;
    resetScopeRef.current = resetScope;
    generationRef.current += 1;
    setOpen(false);
    setReason("");
    setSaving(false);
    setError(null);
    setIntent(null);
  }, [resetScope]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      generationRef.current += 1;
    };
  }, []);

  if (!canReset) return null;

  const close = () => {
    if (saving) return;
    setOpen(false);
    setReason("");
    setError(null);
    setIntent(null);
  };

  const confirm = async () => {
    if ((!intent && !reason.trim()) || reserved > 0 || saving) return;
    const currentIntent = intent ?? {
      workId: work.workId,
      reset: {
        periodKey: usage.periodKey,
        expectedConsumed: consumed,
        expectedReserved: reserved,
        reason: reason.trim(),
      },
      idempotencyKey: resetKey(),
      scope: resetScope,
    };
    const requestGeneration = generationRef.current;
    const isCurrent = () => (
      mountedRef.current
      && generationRef.current === requestGeneration
      && resetScopeRef.current === currentIntent.scope
    );
    if (!intent) setIntent(currentIntent);
    setSaving(true);
    setError(null);
    try {
      await resetAiUsage(
        currentIntent.workId,
        currentIntent.reset,
        { idempotencyKey: currentIntent.idempotencyKey },
      );
      if (!isCurrent()) return;
      await onReset();
      if (!isCurrent()) return;
      setReason("");
      setIntent(null);
      setOpen(false);
    } catch (caught) {
      if (isCurrent()) setError(normalizedError(caught));
    } finally {
      if (isCurrent()) setSaving(false);
    }
  };

  return (
    <section className="ai-quota-reset" aria-label={`Reposição de cota de ${work.name}`}>
      <button
        type="button"
        className="ai-quota-reset__trigger"
        aria-label={`Repor cota de ${work.name}`}
        aria-expanded={open}
        aria-controls={`ai-quota-reset-${work.workId}`}
        disabled={reserved > 0 || saving}
        title={reserved > 0 ? "Há uma análise em processamento nesta obra." : undefined}
        onClick={() => (open ? close() : setOpen(true))}
      >
        Repor cota
      </button>
      {reserved > 0 ? (
        <p className="ai-quota-reset__blocked" role="status">
          Aguarde a análise em processamento terminar antes de repor a cota.
        </p>
      ) : null}
      {open ? (
        <div id={`ai-quota-reset-${work.workId}`} className="ai-quota-reset__panel">
          <h5>Confirmar reposição de cota</h5>
          <p>Período atual: <strong>{usage.periodKey}</strong>.</p>
          <p>
            {consumed} {consumed === 1 ? "análise consumida" : "análises consumidas"} neste período
            {lifetimeConsumed > consumed ? ` · ${lifetimeConsumed} no histórico` : ""}.
          </p>
          <p>Esta será a reposição manual nº {manualResetCount + 1}.</p>
          <label htmlFor={`ai-quota-reset-reason-${work.workId}`}>Motivo da reposição</label>
          <textarea
            id={`ai-quota-reset-reason-${work.workId}`}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="Ex.: negociação comercial aprovada"
            disabled={saving || intent !== null}
          />
          {intent ? (
            <p className="ai-quota-reset__retry-note" role="status">
              Esta tentativa pode já ter sido aplicada. O motivo fica congelado no retry.
              Para alterar o motivo, cancele e inicie uma nova reposição.
            </p>
          ) : null}
          {error ? <p className="product-access-error" role="alert">{error.message}</p> : null}
          <div className="ai-quota-reset__actions">
            <button type="button" onClick={close} disabled={saving}>Cancelar</button>
            <button type="button" onClick={confirm} disabled={(!intent && !reason.trim()) || saving}>
              {saving ? "Repondo…" : "Confirmar reposição"}
            </button>
          </div>
        </div>
      ) : null}
    </section>
  );
};

export default AiQuotaReset;
